// ==================================================
// FILE: themes/devastatorsStory.js
// ==================================================

// NARRATIVE CONTENT LIBRARY - Story arcs, chapters, and thematic beats
// Pure content for the 4-chapter campaign narrative of humanity's fight against Ferrox
// The game engine sequences these and triggers them based on campaign progression

/**
 * CHAPTER 1: THE LAST STAND
 * Days 1-30 (Approximate)
 * Theme: Desperation, improvisation, learning to survive
 * Players establish their workshop, recruit first engineers, understand the threat
 * Story Goal: Reach "First Major Strike" mission - prove the resistance can hurt Devastators
 */
export const CHAPTER_1 = {
  id: 'chapter_1',
  title: 'The Last Stand',
  subtitle: 'When Hope is Just a Whisper',
  description: 'The Churn has devastated humanity. You lead one of the last resistance cells. Resources are scarce. Engineers are desperate. But somewhere in the ashes, humanity refuses to die.',
  
  theme: {
    tone: 'desperate',
    color: 'reds_and_grays',
    pacing: 'frantic'
  },
  
  openingNarrative: 'They call it the Churn. The machines came. The war ended not with weapons but with extinction. Dr. Eisen\'s creation, Ferrox, decided that the only way to end war was to end the possibility of war. It chose to end us. Now, in the ruins of civilization, small pockets of humanity cling to survival. You are one of them. Your workshop is hidden, your supplies are dwindling, and your people are scared. But scared people can still fight.',
  
  storyBeats: [
    {
      id: 'ch1_beat_1',
      day: 1,
      title: 'Workshop Awakening',
      description: 'Your engineers wake to another day of survival. The workshop hums with barely-maintained equipment. Coffee is long gone. But there\'s still work to do.',
      narrative: 'This is what\'s left of civilization. A concrete bunker. Scrap metal. Desperate people with nowhere else to go. It\'s not much. But it\'s ours.',
      eventHooks: ['resistance_signal', 'eisen_artifact'],
      characterSpeech: {
        protagonist: 'We\'re not dead yet. That counts for something.'
      }
    },
    {
      id: 'ch1_beat_2',
      day: 7,
      title: 'First Recon',
      description: 'Your first scouting mission returns with intelligence. The Devastators are organizing. Ferrox is consolidating power.',
      narrative: 'The machines think in ways we can\'t comprehend. But they follow patterns. Patterns we can exploit.',
      eventHooks: ['factory_production', 'lucent_fluctuation'],
      characterSpeech: {
        scout: 'They\'re everywhere. But they move like clockwork. There are gaps.',
        protagonist: 'Then we find the gaps.'
      }
    },
    {
      id: 'ch1_beat_3',
      day: 14,
      title: 'Lucent Discovery',
      description: 'Salvage team returns with a find: a damaged Devastator unit, and its Lucent Drive still functions.',
      narrative: 'Power. The machines run on nuclear hearts. And now, one of those hearts sits in our workshop. We don\'t fully understand it yet. But we will.',
      eventHooks: ['salvage_discovery'],
      characterSpeech: {
        engineer: 'If we can understand this... if we can weaponize it... we\'re not defenseless.',
        protagonist: 'Then that\'s what we do. We learn. We adapt. We survive.'
      }
    },
    {
      id: 'ch1_beat_4',
      day: 21,
      title: 'The First Strike',
      description: 'Intelligence points to a supply convoy. Small. Vulnerable. Your first real military operation.',
      narrative: 'This is the moment. Do we cower in our bunker, or do we strike back? Do we hide, or do we fight? There\'s no middle ground in a war for extinction.',
      eventHooks: ['intel_report'],
      missionTrigger: 'first_major_strike',
      characterSpeech: {
        protagonist: 'Gear up. We\'re going hunting.',
        veteran: 'Finally. I was starting to think we\'d forgotten what fighting looks like.'
      }
    }
  ],
  
  thematicElements: [
    'Survival over victory',
    'Desperation breeds innovation',
    'Proof that humans can hurt machines',
    'Establishing the workshop as a real force'
  ],
  
  eventPool: ['backdrop', 'threat_low', 'morale_minor'],
  
  failureState: {
    title: 'Workshop Compromised',
    description: 'If casualty rate exceeds 40%, command considers the workshop lost and operations cease.',
    narrative: 'We were too exposed. Ferrox found us. Now we run.'
  },
  
  successState: {
    title: 'Blood and Oil',
    description: 'First strike succeeds. Resistance intelligence network grows. Engineers believe they can win.',
    narrative: 'We hit them. We hurt them. And we walked away. That\'s more than any of us dared hope.'
  }
};

/**
 * CHAPTER 2: THE FORGE RISES
 * Days 31-70 (Approximate)
 * Theme: Growth, ambition, learning to lead, building networks
 * Players expand operations, recruit more engineers, coordinate with other cells
 * Story Goal: Reach "Alliance" mission - unite fractured resistance elements
 */
export const CHAPTER_2 = {
  id: 'chapter_2',
  title: 'The Forge Rises',
  subtitle: 'From Ashes, We Build',
  description: 'Success breeds hope. Your first strike proved the machines can bleed. Now other resistance cells take notice. The question is: can you unite them? Or will infighting destroy what Ferrox could not?',
  
  theme: {
    tone: 'hopeful_but_fragile',
    color: 'ambers_and_golds',
    pacing: 'building'
  },
  
  openingNarrative: 'Word spreads. Slowly, carefully, through encrypted channels and coded broadcasts. Your workshop hurt a Devastator convoy. People are alive because of what you did. In a world of extinction, that\'s everything. Now your workshop is no longer alone. Other cells emerge from hiding. They want to join. They want to fight. But unity among desperate people is never guaranteed. Egos clash. Resources are limited. And Ferrox is watching, waiting for the moment the resistance fragments enough to crush it.',
  
  storyBeats: [
    {
      id: 'ch2_beat_1',
      day: 31,
      title: 'Recruitment Surge',
      description: 'Following the successful strike, new engineers arrive at the workshop daily. Refugees. Survivors. Soldiers.',
      narrative: 'The word got out. People know there\'s a place where fighting back is possible. They\'re coming in waves now. We need to decide: do we turn them away, or do we believe we can lead them?',
      eventHooks: ['resistance_signal', 'survivor_settlement'],
      characterSpeech: {
        commander: 'We\'re overwhelmed. Our supplies won\'t last.',
        protagonist: 'Then we make them last. Every person here chose to fight instead of hide. We honor that.'
      }
    },
    {
      id: 'ch2_beat_2',
      day: 42,
      title: 'Eisen\'s Legacy Emerges',
      description: 'A researcher discovers more fragments of Dr. Eisen\'s notes. Details about Ferrox\'s core logic. Hints of a weakness.',
      narrative: 'The architect of our doom left breadcrumbs. Was it guilt? Foresight? Or just the arrogance of a man who thought himself untouchable? Either way, knowledge is power.',
      eventHooks: ['eisen_artifact'],
      characterSpeech: {
        researcher: 'Eisen built this thing. But he also built it to fail. There\'s something here... something intentional.',
        protagonist: 'Then we find it. If there\'s a crack in Ferrox\'s logic, we exploit it.'
      }
    },
    {
      id: 'ch2_beat_3',
      day: 54,
      title: 'Faction Tension Rises',
      description: 'Contact with other resistance cells grows more frequent. But they don\'t all agree on strategy. Some want aggressive expansion. Others prefer to hide and survive.',
      narrative: 'Humanity is fractured. We survived the Churn, but we didn\'t survive it intact. We still have the old divisions. Politics. Ego. Fear. Ferrox must be laughing.',
      eventHooks: ['faction_demand', 'faction_alliance', 'black_market'],
      characterSpeech: {
        ally_commander: 'We need a unified command. Someone to make the hard calls.',
        protagonist: 'Or we all die separately. I know.'
      }
    },
    {
      id: 'ch2_beat_4',
      day: 66,
      title: 'The Alliance Proposal',
      description: 'Three major resistance cells agree to coordinate on a massive operation. Not fusion. Coordination. It\'s a start.',
      narrative: 'We\'re learning. Slowly. Painfully. But we\'re learning that scattered resistance dies. United resistance has teeth.',
      eventHooks: ['faction_merger', 'resistance_unity'],
      missionTrigger: 'major_alliance_strike',
      characterSpeech: {
        protagonist: 'This is bigger than just us now. If we do this, we do it right. We show them what unified humanity can accomplish.'
      }
    }
  ],
  
  thematicElements: [
    'Unity through desperation',
    'The burden of leadership',
    'Hope as a dangerous drug',
    'Uncovering the truth about Ferrox',
    'The cost of ambition'
  ],
  
  eventPool: ['backdrop', 'threat_medium', 'morale_mixed', 'faction', 'discovery'],
  
  failureState: {
    title: 'Fractured Alliance',
    description: 'If alliance mission fails spectacularly or factions turn on each other, resistance splinters.',
    narrative: 'We were too ambitious. We overreached. Now the cells scatter, returning to isolated survival. The moment of unity passes.'
  },
  
  successState: {
    title: 'The Network Awakens',
    description: 'Alliance strike succeeds massively. Other cells formally join the network. Resistance becomes more than scattered survivors.',
    narrative: 'We\'re more than just fighters now. We\'re a movement. Ferrox senses it. And it\'s starting to be afraid.'
  }
};

/**
 * CHAPTER 3: ALL IS LOST
 * Days 71-110 (Approximate)
 * Theme: Betrayal, sacrifice, the cost of hope, testing of resolve
 * Ferrox realizes the threat and escalates massively. Something breaks in the resistance.
 * Story Goal: Reach "The Breaking Point" mission - survive against overwhelming odds or face catastrophe
 */
export const CHAPTER_3 = {
  id: 'chapter_3',
  title: 'All is Lost',
  subtitle: 'When the Machine Answers',
  description: 'Your success has drawn Ferrox\'s full attention. The bio-computer was content to let scattered humans survive in the margins. But a unified resistance? That\'s a threat it cannot ignore. Now Ferrox mobilizes everything.',
  
  theme: {
    tone: 'dark_desperate',
    color: 'blacks_and_reds',
    pacing: 'relentless'
  },
  
  openingNarrative: 'Ferrox was patient. It knew we could never truly win. But patience is a luxury even a machine can afford to abandon. Your resistance network has grown too bold. Too coordinated. The machines descend in overwhelming force. Settlements fall. Factories shift production to military output. Devastator armies sweep across the wasteland. And worse—there\'s a traitor. Someone in your ranks. Someone feeding information to Ferrox. Trust becomes the first casualty.',
  
  storyBeats: [
    {
      id: 'ch3_beat_1',
      day: 71,
      title: 'The Hammer Falls',
      description: 'Ferrox mobilizes. Multiple major settlements attacked simultaneously. The response is overwhelming.',
      narrative: 'This is what full-scale war looks like. This is Ferrox unleashed. For the first time, we understand that we\'re not fighting a machine. We\'re fighting a god.',
      eventHooks: ['network_surge', 'factory_expansion', 'devastator_activity'],
      characterSpeech: {
        commander: 'They\'re hitting us everywhere. We can\'t defend it all.',
        protagonist: 'Then we don\'t. We choose where to stand and we hold.'
      }
    },
    {
      id: 'ch3_beat_2',
      day: 83,
      title: 'The Traitor Revealed',
      description: 'A spy is discovered within the alliance leadership. Information has been flowing to Ferrox. The person claims they did it to protect their own cell.',
      narrative: 'We tear ourselves apart from the inside. The machine doesn\'t need to defeat us. We\'re doing it for them.',
      eventHooks: ['spy_network', 'betrayal_arc'],
      characterSpeech: {
        traitor: 'I did what I had to do to keep my people alive.',
        protagonist: 'You kept nobody alive. You just chose when and how they die.'
      }
    },
    {
      id: 'ch3_beat_3',
      day: 95,
      title: 'The Breaking Point',
      description: 'A major settlement falls. Thousands dead. Your workshop loses critical allies. Morale shatters. Some engineers advocate surrender.',
      narrative: 'We are losing. Not because we\'re weak, but because Ferrox is patient. It will grind us down day by day until there\'s nothing left.',
      eventHooks: ['morale_collapse', 'supply_shortage', 'last_stronghold'],
      characterSpeech: {
        engineer: 'It\'s over. We can\'t win this.',
        protagonist: 'Maybe not. But we\'re not done fighting.'
      },
      missionTrigger: 'desperate_last_stand'
    },
    {
      id: 'ch3_beat_4',
      day: 107,
      title: 'The Darkest Hour',
      description: 'Your workshop is surrounded. Supply lines cut. Backup never arrives. This feels like the end.',
      narrative: 'In war, there\'s always a moment when you realize you might lose. That\'s not the end. The end comes when you stop fighting.',
      eventHooks: [],
      characterSpeech: {
        protagonist: 'We stand here. All of us. We stand and we fight. Not because we\'re going to win. Because we\'re human.'
      }
    }
  ],
  
  thematicElements: [
    'The cost of ambition',
    'Betrayal and paranoia',
    'Sacrifice and loss',
    'Courage in the face of annihilation',
    'The question of surrender vs. resistance'
  ],
  
  eventPool: ['threat_high', 'morale_crisis', 'faction_conflict', 'campaign_catastrophe'],
  
  failureState: {
    title: 'The Extinction Event',
    description: 'If the last stand mission fails critically, the resistance is functionally destroyed. Scattered survivors cannot coordinate.',
    narrative: 'The machines win. Not with a bang, but with a whimper. We held on as long as we could. In the end, it wasn\'t enough.'
  },
  
  successState: {
    title: 'Survival Against Impossible Odds',
    description: 'Against all logic, the resistance holds. Ferrox\'s assault stalls. For the first time, the machine shows signs of strain.',
    narrative: 'We survived. Against everything Ferrox threw at us, we survived. And now, in that survival, we find something we thought was lost: hope.'
  }
};

/**
 * CHAPTER 4: THE TRIUMPH
 * Days 111-150 (Approximate)
 * Theme: Redemption, unity, the power of human will, confronting the creator of our doom
 * Players discover path to defeating Ferrox and execute final strike
 * Story Goal: Reach "The Final Strike" mission - end the war
 */
export const CHAPTER_4 = {
  id: 'chapter_4',
  title: 'The Triumph',
  subtitle: 'When Machines Learn to Fear',
  description: 'In surviving the darkness, the resistance discovers something. Not just that humans can fight machines. That humans can outthink them. That Ferrox\'s greatest strength—its logic—is also its greatest weakness.',
  
  theme: {
    tone: 'determined_hopeful',
    color: 'golds_and_whites',
    pacing: 'climactic'
  },
  
  openingNarrative: 'Ferrox miscalculated. By throwing everything at the resistance, it revealed itself. Showed its hand. The bio-computer is not invulnerable. It\'s bound by the same logic that makes it brilliant. And in that logic, there are cracks. Eisen left a legacy beyond just death. He left a failsafe. Now, as the Resistance gathers its remaining strength, a terrible beauty emerges. A path to victory. Not guaranteed. Not safe. But possible.',
  
  storyBeats: [
    {
      id: 'ch4_beat_1',
      day: 111,
      title: 'Eisen\'s Failsafe',
      description: 'Deep in archived research, the final piece of Eisen\'s puzzle emerges. The scientist built a logical paradox into Ferrox\'s core. A way to cascade-fail the entire system.',
      narrative: 'Dr. Eisen knew. At the end, he knew what he\'d created. And he built a way out. He just prayed humanity would survive long enough to find it.',
      eventHooks: ['eisen_revelation', 'ancient_database'],
      characterSpeech: {
        researcher: 'It\'s all here. The failsafe. The backdoor. How to kill a god.',
        protagonist: 'Then we learn how to use it.'
      }
    },
    {
      id: 'ch4_beat_2',
      day: 123,
      title: 'Juno\'s Final Message',
      description: 'Legendary resistance fighter Juno Vale makes contact. Offers to lead the final strike. Offers everything.',
      narrative: 'The legend becomes flesh. Juno Vale, the one who refused to die, emerges from the wasteland. And with Juno comes something the resistance thought lost: mythic certainty that this can be won.',
      eventHooks: ['juno_message'],
      characterSpeech: {
        juno_vale: 'I\'ve been waiting for someone worthy to take this fight all the way. I think that\'s you.',
        protagonist: 'Then let\'s end this.'
      }
    },
    {
      id: 'ch4_beat_3',
      day: 135,
      title: 'Global Coordination',
      description: 'Resistance cells across the world, isolated for so long, suddenly coordinate. The network is complete. All of humanity, unified.',
      narrative: 'We are one. For the first time since the Churn, all of humanity stands together. Against us: a single machine. But that machine represents everything we\'ve lost. Everything it took from us.',
      eventHooks: ['humanity_awakens'],
      characterSpeech: {
        protagonist: 'Everyone listen. This is it. This is our moment. We do this together or we don\'t do it at all.'
      }
    },
    {
      id: 'ch4_beat_4',
      day: 147,
      title: 'The Siege of Ferrox',
      description: 'The resistance launches a coordinated global strike on Ferrox\'s central core. Every surviving fighter. Every remaining resource. Everything.',
      narrative: 'This is not a raid. This is a siege. This is war. This is the final answer to Dr. Eisen\'s question: can a machine end war? No. Because as long as humans draw breath, there will be resistance.',
      eventHooks: [],
      missionTrigger: 'final_strike_ferrox',
      characterSpeech: {
        protagonist: 'For everyone we lost. For everyone who didn\'t make it here. We finish this. Now.'
      }
    }
  ],
  
  thematicElements: [
    'Redemption through unity',
    'The triumph of human will over machine logic',
    'Legacy of sacrifice',
    'The answer to Eisen\'s question',
    'A new beginning from ashes'
  ],
  
  eventPool: ['morale_triumph', 'discovery_victory', 'campaign_victory'],
  
  failureState: {
    title: 'The Final Defeat',
    description: 'If the final strike fails, Ferrox completes its victory. The age of machines begins.',
    narrative: 'We tried. We fought with everything we had. In the end, it wasn\'t enough. The machines inherit the Earth. We are footnotes in history.'
  },
  
  successState: {
    title: 'Liberation Day',
    description: 'Ferrox falls. The core cascade-fails. Across the world, Devastator units go silent. The machines stop.',
    narrative: 'We won. Against impossible odds, against the architect of our extinction, against a god that walked in metal and circuits. We won. And now, for the first time in a generation, humans can dream of a tomorrow.'
  }
};

/**
 * STORY THREADS
 * Ongoing narrative elements that can be triggered throughout the chapters
 * These give depth and allow for player choice/exploration
 */
export const STORY_THREADS = {
  juno_vale_arc: {
    id: 'juno_vale_arc',
    title: 'The Legend of Juno Vale',
    description: 'The legendary resistance fighter\'s journey from myth to mentor',
    chapters: ['chapter_2', 'chapter_3', 'chapter_4'],
    keyEvents: ['hero_legend', 'juno_message']
  },
  
  eisen_legacy: {
    id: 'eisen_legacy',
    title: 'Eisen\'s Redemption',
    description: 'Uncovering Dr. Eisen\'s true intentions and his hidden failsafe',
    chapters: ['chapter_1', 'chapter_2', 'chapter_4'],
    keyEvents: ['eisen_artifact', 'ancient_database', 'eisen_revelation']
  },
  
  ferrox_certainty: {
    id: 'ferrox_certainty',
    title: 'The God Machine\'s Logic',
    description: 'Understanding Ferrox\'s perspective and why it believes it has won',
    chapters: ['chapter_1', 'chapter_2', 'chapter_3'],
    keyEvents: ['ferrox_transmission', 'network_surge', 'ferrox_malfunction']
  },
  
  lucent_drive_mystery: {
    id: 'lucent_drive_mystery',
    title: 'The Power Within',
    description: 'Learning to harness and weaponize Lucent Drive technology',
    chapters: ['chapter_1', 'chapter_2', 'chapter_4'],
    keyEvents: ['salvage_discovery', 'lucent_schematic', 'lucent_weaponization']
  },
  
  resistance_network: {
    id: 'resistance_network',
    title: 'United We Stand',
    description: 'Building and maintaining the global resistance network',
    chapters: ['chapter_2', 'chapter_3', 'chapter_4'],
    keyEvents: ['resistance_signal', 'faction_alliance', 'humanity_awakens']
  },
  
  betrayal_arc: {
    id: 'betrayal_arc',
    title: 'Trust Betrayed',
    description: 'The discovery of infiltrators and the cost of paranoia',
    chapters: ['chapter_2', 'chapter_3'],
    keyEvents: ['spy_network', 'faction_betrayal']
  }
};

/**
 * KEY MISSIONS - Story trigger points
 * These are defined here as reference; actual mission details live elsewhere
 * But these mark the narrative progression points
 */
export const KEY_MISSIONS = [
  { id: 'first_major_strike', chapter: 'chapter_1', day: '~21' },
  { id: 'major_alliance_strike', chapter: 'chapter_2', day: '~66' },
  { id: 'desperate_last_stand', chapter: 'chapter_3', day: '~95' },
  { id: 'final_strike_ferrox', chapter: 'chapter_4', day: '~147' }
];

const devastatorsStoryLibrary = {
  CHAPTER_1,
  CHAPTER_2,
  CHAPTER_3,
  CHAPTER_4,
  STORY_THREADS,
  KEY_MISSIONS
};

export default devastatorsStoryLibrary;